import {closeDialog, openDialog, showNotification} from 'platform/components';
import {Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isArray, isNilOrEmpty, isString} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  DmsDataGrid,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  logError,
  useWarehouseDataGridTreeFolder,
} from '@dms/shared';
import {FullscreenDataGridWrapper} from '@dms/warehouse/shared-components';

import {useBoolean} from 'shared/web';

import type {ActionCallback, RowData} from 'features/eag-grid';

import {AddToSupplierOrderModal} from './AddToSupplierOrderModal';
import {CreateSupplierOrderForm} from './CreateSupplierOrderForm';
import {PurchaseListExternalFilter} from './PurchaseListExternalFilter';

type ExternalFilters = {
  warehouseId?: string | string[];
};

const PURCHASE_LIST_EXTERNAL_FILTER_ID = 'purchase-list-external-filter';

const PREDEFINED_COLUMNS = ['warehouseId', 'warehouseName'] as const;

export function PurchaseList() {
  const [folderTreeRef, dataGridModifier] = useWarehouseDataGridTreeFolder();
  const [isAddToSupplierModalVisible, , , toggleAddToSupplierModal] = useBoolean();
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [externalFilters, setExternalFilters] = useState<ExternalFilters>({});

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
  }) => {
    const isBulk = isArray(rowId);
    const rowsIds = isArray(rowId) ? rowId : [rowId];

    match(actionKey)
      .with('createSupplierOrder', () => {
        if (isBulk && isNilOrEmpty(externalFilters.warehouseId)) {
          return showNotification.error(
            i18n.t('page.warehousePurchaseList.labels.warehouseIsRequiredNotification')
          );
        }

        const warehouseId = getColumnData<{key: string}>('warehouseId')?.value.key;

        if (!isString(warehouseId)) {
          return logError('warehouseId must be a string');
        }

        openDialog(
          <CreateSupplierOrderForm
            itemId={rowsIds}
            onClose={() => closeDialog('createSupplierOrderDialog')}
            warehouseId={warehouseId}
            data-testid={testIds.warehouse.purchaseList('createNewSupplierOrder')}
          />,
          {
            id: 'createSupplierOrderDialog',
            title: i18n.t('general.labels.createNew'),
            scrollBehavior: 'outside',
          }
        );
      })
      .with('addToSupplierOrder', () => {
        setSelectedRowIds(rowsIds);
        toggleAddToSupplierModal();
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const extractOrderId = (rows: RowData[]) =>
    rows.filter((row) => 'id' in row && isString(row.id)).map((row) => row.id);

  const handleRowSelectionChange = (rows: RowData[]) => {
    setSelectedRowIds(extractOrderId(rows));
  };

  const handleExternalFiltersChange = (values: ExternalFilters) => {
    setExternalFilters(values);
  };

  return (
    <>
      <FullscreenDataGridWrapper
        externalFilterId={PURCHASE_LIST_EXTERNAL_FILTER_ID}
        folderTreeRef={folderTreeRef}
        data-testid={testIds.warehouse.purchaseList('externalFilterWithFolderTree')}
      >
        <DmsDataGrid
          // DG must be re-rendered on folder change to update the query modifier
          // eslint-disable-next-line no-restricted-syntax
          key={dataGridModifier?.key}
          gridCode={WAREHOUSE_GRID_CODES.warehousePurchaseList}
          onFilterChanged={handleExternalFiltersChange as (filters: unknown) => void}
          externalFilterId={PURCHASE_LIST_EXTERNAL_FILTER_ID}
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          onRowSelectionChange={handleRowSelectionChange}
          queryModifier={dataGridModifier?.queryModifier}
          _useAsLastResort_definitionBehaviorOverrides={{filterMode: 'NATIVE'}}
          filterComponent={PurchaseListExternalFilter}
          data-testid={testIds.warehouse.purchaseList('list')}
        />
      </FullscreenDataGridWrapper>
      <Show when={isAddToSupplierModalVisible}>
        <AddToSupplierOrderModal
          itemId={selectedRowIds}
          warehouseId={
            isArray(externalFilters.warehouseId)
              ? (externalFilters.warehouseId?.[0] as string)
              : (externalFilters.warehouseId as string)
          }
          onClose={toggleAddToSupplierModal}
          data-testid={testIds.warehouse.purchaseList('addItems')}
        />
      </Show>
    </>
  );
}
